import { useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { CheckCircle, Warning, XCircle, EnvelopeSimple, ArrowsClockwise, ProhibitInset, Eye } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

/**
 * Phase 259 — Inline badge showing whether the auto "we're on it"
 * acknowledgment email was sent for this Job or Job Request.
 *
 * Props:
 *   entityType: "job" | "job_request"
 *   entity: object with ack_email_status, ack_email_sent_to, ack_email_sent_at, ack_email_cc
 *   onChange: () => refresh callback
 */
const STATE_CFG = {
  sent: {
    Icon: CheckCircle, weight: "fill",
    bg: "bg-emerald-50 dark:bg-emerald-900/20",
    border: "border-emerald-200 dark:border-emerald-800/50",
    text: "text-emerald-700 dark:text-emerald-300",
    label: "Acknowledgment email sent",
  },
  skipped_no_recipient: {
    Icon: Warning, weight: "regular",
    bg: "bg-amber-50 dark:bg-amber-900/20",
    border: "border-amber-200 dark:border-amber-800/50",
    text: "text-amber-700 dark:text-amber-300",
    label: "No requester email — couldn't send",
  },
  skipped_disabled: {
    Icon: ProhibitInset, weight: "regular",
    bg: "bg-slate-50 dark:bg-slate-800/40",
    border: "border-slate-200 dark:border-slate-700/60",
    text: "text-slate-600 dark:text-slate-300",
    label: "Acknowledgment template disabled",
  },
  failed: {
    Icon: XCircle, weight: "fill",
    bg: "bg-red-50 dark:bg-red-900/20",
    border: "border-red-200 dark:border-red-800/50",
    text: "text-red-700 dark:text-red-300",
    label: "Send failed",
  },
  // Legacy entities created before Phase 290 have no ack_email_status stamped
  // at all. Instead of hiding the whole component (which was the pre-Phase-290
  // behaviour and left admins with no way to trigger the email), we render a
  // neutral "not yet sent" state with an active Send now button.
  not_attempted: {
    Icon: EnvelopeSimple, weight: "regular",
    bg: "bg-slate-50 dark:bg-slate-800/40",
    border: "border-slate-200 dark:border-slate-700/60",
    text: "text-slate-600 dark:text-slate-300",
    label: "Acknowledgment email not yet sent",
  },
};

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (!Number.isFinite(ms) || ms < 0) return "";
    const m = Math.floor(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return ""; }
}

export default function AckEmailStatusBadge({ entityType, entity, onChange }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!entity) return null;
  // Legacy jobs/JRs created before the ack-email pipeline existed have no
  // status stamped. Show a neutral "not yet sent" state instead of hiding
  // the component — otherwise admins have no visible way to trigger a send.
  const status = entity.ack_email_status || "not_attempted";

  const cfg = STATE_CFG[status] || STATE_CFG.not_attempted;
  const Icon = cfg.Icon;
  const canResend = ["super_admin", "admin", "project_manager"].includes(user?.role);
  const isSent = status === "sent";
  const isLegacy = status === "not_attempted";
  const wasOpened = isSent && !!entity.ack_email_first_opened_at;
  const recipientLabel = entity.requester_contact_name
    || (entity.ack_email_sent_to ? entity.ack_email_sent_to.split("@")[0] : "the recipient");
  // Make the "sent" badge upgrade to a stronger "read" tone when opened
  const finalBg = wasOpened ? "bg-emerald-100 dark:bg-emerald-900/30" : cfg.bg;
  const finalBorder = wasOpened ? "border-emerald-300 dark:border-emerald-700" : cfg.border;

  const handleResend = async () => {
    if (!canResend) return;
    if (status === "skipped_no_recipient") {
      toast.error("Add a requester contact email first, then click Resend.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await API.post(`/email-templates/acknowledgment/resend/${entityType}/${entity.id}`);
      const newStatus = data?.status?.ack_email_status;
      if (newStatus === "sent") toast.success(`Acknowledgment email sent to ${data.status.ack_email_sent_to}`);
      else if (newStatus === "skipped_disabled") toast.error("Template is disabled — turn it on in Settings → Acknowledgment Email.");
      else if (newStatus === "skipped_no_recipient") toast.error("No requester email on this item.");
      else if (newStatus === "failed") toast.error("Send failed — check backend logs.");
      else toast.success("Triggered");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Resend failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      className={`mb-3 rounded-md border ${finalBorder} ${finalBg} px-3 py-2 flex items-center gap-2.5 flex-wrap`}
      data-testid="ack-email-status-badge"
    >
      <EnvelopeSimple size={14} className={cfg.text} />
      {wasOpened ? <Eye size={14} weight="fill" className="text-emerald-700 dark:text-emerald-300" /> : <Icon size={14} weight={cfg.weight} className={cfg.text} />}
      <div className="flex-1 min-w-[160px]">
        <p className={`text-xs font-medium ${wasOpened ? "text-emerald-700 dark:text-emerald-300" : cfg.text}`} data-testid="ack-email-status-label">
          {wasOpened ? `Read by ${recipientLabel}` : cfg.label}
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="ack-email-status-detail">
          {wasOpened && (
            <>
              First opened <strong>{timeAgo(entity.ack_email_first_opened_at)}</strong>
              {entity.ack_email_opened_count > 1 ? ` · viewed ${entity.ack_email_opened_count}× total` : ""}
              {entity.ack_email_sent_to ? <> · sent to {entity.ack_email_sent_to}</> : null}
            </>
          )}
          {isSent && !wasOpened && entity.ack_email_sent_to && (
            <>To <strong>{entity.ack_email_sent_to}</strong>
              {entity.ack_email_cc?.length ? <> · cc {entity.ack_email_cc.join(", ")}</> : null}
              {entity.ack_email_sent_at ? <> · {fmt(entity.ack_email_sent_at)}</> : null}
              <span className="ml-1 text-amber-600 dark:text-amber-400">· not yet read</span>
            </>
          )}
          {!isSent && status === "skipped_no_recipient" && "Add a requester contact email and click Resend below."}
          {!isSent && status === "skipped_disabled" && "Enable the template in Settings → Acknowledgment Email to start auto-sending."}
          {!isSent && status === "failed" && (entity.ack_email_error || "An error stopped delivery — try again.")}
          {isLegacy && "This item was created before auto-acknowledgments were enabled. Click Send now to notify the requester."}
        </p>
        {/* Non-blocking note — e.g. custom `from_email` domain wasn't verified in
            Resend so we automatically fell back to the platform default sender. */}
        {entity.ack_email_note && (
          <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-0.5" data-testid="ack-email-note">
            Note: {entity.ack_email_note}
          </p>
        )}
      </div>
      {canResend && (
        <Button
          size="sm" variant="outline" onClick={handleResend} disabled={busy}
          className="h-7 text-[10px]"
          data-testid="ack-email-resend-btn"
        >
          <ArrowsClockwise size={11} className="mr-1" />
          {isSent ? "Resend" : "Send now"}
        </Button>
      )}
    </div>
  );
}
